import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  ACCENT_PRESETS,
  DEFAULT_ACCENT,
  accentCssVars,
  normalizeHex,
} from '../theme/accent'
import {
  normalizeScheme,
  schemeCssVars,
  type ColorScheme,
} from '../theme/scheme'

export const useThemeStore = defineStore(
  'theme',
  () => {
    const accentHex = ref(DEFAULT_ACCENT)
    const colorScheme = ref<ColorScheme>('dark')

    /**
     * Stores a 6-digit hex accent; ignored if the string is not valid.
     */
    function setAccent(hex: string): void {
      const next = normalizeHex(hex)
      if (!next) return
      accentHex.value = next
    }

    /**
     * Switches the Material light or dark surface palette.
     */
    function setScheme(scheme: ColorScheme): void {
      colorScheme.value = normalizeScheme(scheme)
    }

    /** Flips between light and dark. */
    function toggleScheme(): void {
      colorScheme.value = colorScheme.value === 'dark' ? 'light' : 'dark'
    }

    const cssVars = computed(() => {
      const scheme = normalizeScheme(colorScheme.value)
      return {
        ...schemeCssVars(scheme),
        ...accentCssVars(accentHex.value, scheme),
      }
    })

    const presetId = computed(() => {
      const current = accentHex.value.toUpperCase()
      return ACCENT_PRESETS.find((p) => p.hex.toUpperCase() === current)?.id
        ?? null
    })

    return {
      accentHex,
      colorScheme,
      setAccent,
      setScheme,
      toggleScheme,
      cssVars,
      presetId,
    }
  },
  {
    persist: {
      paths: ['accentHex', 'colorScheme'],
    },
  },
)
